import { api } from './api';

/**
 * Creates a Stripe Setup Intent to securely collect payment method details
 * @returns Promise containing the setup intent object with client_secret
 */
export const createSetupIntent = async () => {
  try {
    const response = await api.post('/api/payments/create-setup-intent');
    return response.data;
  } catch (error) {
    console.error('Error creating setup intent:', error);
    throw error;
  }
};

/**
 * Creates a checkout session for updating payment methods
 * @param businessId ID of the business to update payment method for
 * @returns Promise containing session data with redirect URL
 */
export const createUpdateSession = async (businessId: string) => {
  try {
    const response = await api.post('/api/payments/create-update-session', { businessId });
    return response.data;
  } catch (error) {
    console.error('Error creating update session:', error);
    throw error;
  }
};

/**
 * Associates a payment method with a business account
 * @param params Object containing business_id and payment_method_id
 * @returns Promise with the updated payment method data
 */
export const updateBusinessPaymentMethod = async (params: { 
  business_id: string; 
  payment_method_id: string;
}) => {
  try {
    const response = await api.post('/api/payments/update-payment-method', params);
    return response.data;
  } catch (error) {
    console.error('Error updating payment method:', error);
    throw error;
  }
};

/**
 * Retrieves all payment methods for a business
 * @param businessId The business ID
 * @returns Promise with an array of payment methods
 */
export const getBusinessPaymentMethods = async (businessId: string) => {
  try {
    const response = await api.get(`/api/payments/methods/${businessId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching payment methods:', error);
    throw error;
  }
};

/**
 * Deletes a payment method
 * @param paymentMethodId The payment method ID to delete
 * @returns Promise with the deletion result
 */
export const deletePaymentMethod = async (paymentMethodId: string) => {
  try {
    const response = await api.delete(`/api/payments/methods/${paymentMethodId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting payment method:', error);
    throw error;
  }
};

/**
 * Sets a payment method as the default for a business
 * @param params Object containing business_id and payment_method_id
 * @returns Promise with the updated payment method data
 */
export const setDefaultPaymentMethod = async (params: {
  business_id: string;
  payment_method_id: string;
}) => {
  try {
    const response = await api.post('/api/payments/set-default-method', params);
    return response.data;
  } catch (error) {
    console.error('Error setting default payment method:', error);
    throw error;
  }
};
